import React, { useMemo, useState } from 'react';
import { ToastContainer } from 'react-toastify';

import { Spinner } from '@/components/Elements';
import { Head } from '@/components/Head';
import { Modal } from '@/components/Modal';
import { Table } from '@/components/Table';
import { ErrorProps } from '@/types';
import { formatDate } from '@/utils/functions';
import { notifyError, notifySuccess } from '@/utils/notifications';

import { CashoutForm } from '../components/CashoutForm';
import { TableMenu } from '../components/CashoutMenu';
import { ConfirmCancel } from '../components/ConfirmCancel';
import { useCancelCashout, useGetCashout } from '../hooks';

export const Cashout = () => {
  const [pageSize, setPageSize] = useState(20);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedRange, setAppliedRange] = useState<{
    startDate?: string;
    endDate?: string;
  }>({});

  const {
    data: cashout,
    isLoading,
    isRefetching,
    isFetching,
  } = useGetCashout({
    pageSize,
    startDate: appliedRange.startDate,
    endDate: appliedRange.endDate,
  });

  // Only offer "Load more" when the server actually returned a full page —
  // fewer rows than pageSize means there's nothing further to fetch.
  const canLoadMore = Boolean(cashout && cashout.length >= pageSize);

  const applyDateRange = () => {
    setPageSize(20);
    setAppliedRange({
      startDate: fromDate || undefined,
      endDate: toDate || undefined,
    });
  };

  const clearDateRange = () => {
    setFromDate('');
    setToDate('');
    setPageSize(20);
    setAppliedRange({});
  };

  const loadMore = () => {
    setPageSize((prev) => prev + 50);
  };

  const [isPaidBoxOpen, toggleIsPaidBoxOpen] = useState(false);
  const [isCancelConfirmationOpen, toggleIsCancelConfirmationOpen] =
    useState(false);
  const [selectedCashout, setSelectedCashout] = useState('');

  const {
    mutate: cancelCashout,
    isLoading: isCancelLoading,
    isSuccess: isCancelSuccess,
    isError: isCancelError,
    error: cancelError,
    reset: cancelReset,
  } = useCancelCashout();

  const handlePaid = (_id: string) => {
    setSelectedCashout(_id);
    toggleIsPaidBoxOpen(true);
    //console.log(_id);
  };

  const handleCancel = (_id: string) => {
    //console.log(_id);
    setSelectedCashout(_id);
    toggleIsCancelConfirmationOpen(true);
  };

  const columns = useMemo(
    () => [
      {
        Header: '',
        id: 'menu',
        disableSortBy: true,
        Cell: ({ row }: any) => (
          <div className=''>
            <TableMenu
              _id={row.original._id}
              handleCancel={() => handleCancel(row.original._id)}
              handlePaid={() => handlePaid(row.original._id)}
              cashout={row.original}
            />
          </div>
        ),
      },
      {
        Header: 'User',
        Cell: ({ row }: any) => {
          return (
            <span>
              {row.original.user?.firstName} {row.original.user?.lastName}
            </span>
          );
        },
      },
      {
        Header: 'Amount (₦)',
        accessor: 'amount',
      },
      {
        Header: 'Preferred Currency',
        Cell: ({ row }: any) => {
          return <span>{row.original.user?.preferredCurrency ?? '-'}</span>;
        },
      },
      {
        Header: 'Is Paid',
        Cell: ({ row }: any) => {
          return (
            <span
              className={
                row.original.isPaid ? 'text-green-500' : 'text-red-500'
              }
            >
              {row.original.isPaid ? 'Yes' : 'No'}
            </span>
          );
        },
      },
      {
        Header: 'Address',
        accessor: 'address',
      },
      {
        Header: 'Network',
        accessor: 'network',
      },
      {
        Header: 'Memo/Tag',
        accessor: 'memoTag',
      },
      {
        Header: 'Is Cancelled',
        Cell: ({ row }: any) => {
          return (
            <span
              className={
                row.original.isCancelled ? 'text-red-500' : 'text-green-500'
              }
            >
              {row.original.isCancelled ? 'Yes' : 'No'}
            </span>
          );
        },
      },
      {
        Header: 'Paid By',
        accessor: 'paidBy',
      },

      {
        Header: 'Payment Proof',
        accessor: 'paymentProof',
      },

      {
        Header: 'Date/ Time',
        Cell: ({ row }: any) => {
          return <span>{formatDate(new Date(row.original.createdAt))}</span>;
        },
        // accessor: 'createdAt',
      },
    ],
    [],
  );

  if (isCancelSuccess) {
    notifySuccess('Cashout request has been cancelled successfully', false);
    cancelReset();
  }

  if (isCancelError) {
    notifyError(
      (cancelError as ErrorProps).response?.data.errors[0].msg ||
        (cancelError as ErrorProps).message,
      false,
    );
    cancelReset();
  }

  return (
    <div>
      <Head title='Cashout' />
      {isLoading || isRefetching ? (
        <div className='flex h-screen justify-center items-center'>
          <Spinner size='md' />
        </div>
      ) : (
        <div className='overflow-hidden '>
          <div className='flex flex-wrap items-end gap-3 px-4 mb-4'>
            <div>
              <label
                htmlFor='fromDate'
                className='block text-xs text-slate-500 mb-1'
              >
                From
              </label>
              <input
                id='fromDate'
                type='date'
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className='border border-gray-300 rounded-md p-2 text-sm'
              />
            </div>
            <div>
              <label
                htmlFor='toDate'
                className='block text-xs text-slate-500 mb-1'
              >
                To
              </label>
              <input
                id='toDate'
                type='date'
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className='border border-gray-300 rounded-md p-2 text-sm'
              />
            </div>
            <button
              type='button'
              onClick={applyDateRange}
              className='bg-indigo text-white text-sm h-[38px] px-4 rounded-md'
            >
              Filter
            </button>
            {(appliedRange.startDate || appliedRange.endDate) && (
              <button
                type='button'
                onClick={clearDateRange}
                className='text-sm h-[38px] px-4 rounded-md border border-gray-300'
              >
                Clear
              </button>
            )}
          </div>

          <Table data={cashout ?? []} columns={columns} showPagination={true} />

          {canLoadMore && (
            <div className='flex justify-center my-4'>
              <button
                type='button'
                onClick={loadMore}
                disabled={isFetching}
                className='text-sm h-[38px] px-6 rounded-md border border-indigo text-indigo disabled:opacity-50'
              >
                {isFetching ? 'Loading...' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      )}

      {selectedCashout && (
        <Modal
          isOpen={isPaidBoxOpen}
          closeModal={() => toggleIsPaidBoxOpen(false)}
        >
          <CashoutForm
            cashoutID={selectedCashout}
            closeForm={() => toggleIsPaidBoxOpen(false)}
          />
        </Modal>
      )}

      <ConfirmCancel
        isOpen={isCancelConfirmationOpen}
        setIsOpen={toggleIsCancelConfirmationOpen}
        cancelDocument={cancelCashout}
        isLoading={isCancelLoading}
        selectedData={selectedCashout}
      />
      <ToastContainer />
    </div>
  );
};
